import { useEffect, useId, useMemo, useRef, useState, type CSSProperties } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import MDEditor, { commands } from '@uiw/react-md-editor';
import ReactMarkdown from 'react-markdown';
import {
  ArrowDown,
  ArrowUp,
  BarChart3,
  BookOpen,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Download,
  Flag,
  FileText,
  Globe2,
  Layers,
  List,
  Loader2,
  Maximize,
  PanelRight,
  Play,
  Plus,
  Redo2,
  RefreshCw,
  Save,
  ShieldCheck,
  Sparkles,
  Target,
  Undo2,
  X,
} from 'lucide-react';
import {
  getSectionAnalysis,
  isReportSectionSupported,
  refreshReportSource,
  type ReportDocumentV2,
  type ReportBlock,
  type ReportLayout,
  type ReportSourceSnapshot,
} from '../reports/model';
import { draftReportSection } from '../reports/api';
import { useReportDraft } from '../reports/useReportDraft';
import { compileReportLayout, type LayoutPage } from '../reports/layout';
import ReportCanvas from '../reports/ReportCanvas';
import { loadReportFonts } from '../reports/fonts';
import { REPORT_ACCENT, reportSectionStyle } from '../reports/theme';
import './VisualReportEditor.css';

interface Props {
  initialDocument: ReportDocumentV2;
  initiallySaved: boolean;
  draftKey: string;
  draftIdentityKey?: string;
  legacyDraftKey?: string;
  readOnly?: boolean;
  onSave: (document: ReportDocumentV2) => Promise<void>;
  onRefresh: () => Promise<ReportSourceSnapshot>;
  onReload: () => void;
  onClose: () => void;
}

const layouts: { value: ReportLayout; label: string }[] = [
  { value: 'narrative', label: 'Narrative' },
  { value: 'visual', label: 'Chart & takeaways' },
  { value: 'scorecard', label: 'Metric cards & tables' },
  { value: 'matrix', label: 'SWOT matrix' },
  { value: 'roadmap', label: 'Execution roadmap' },
];
const markdownCommands = [
  commands.title,
  commands.bold,
  commands.italic,
  commands.divider,
  commands.unorderedListCommand,
  commands.orderedListCommand,
  commands.quote,
];

function sectionIcon(id: string) {
  if (id === 'cover') return BookOpen;
  if (id === 'contents') return List;
  if (id === 'foundation') return Flag;
  if (id === 'objectives') return Target;
  if (
    ['external-environment', 'pestel', 'five-forces', 'competitors', 'driving-forces'].includes(id)
  )
    return Globe2;
  if (['swot-capabilities', 'success-factors'].includes(id)) return ShieldCheck;
  if (id === 'execution-progress') return BarChart3;
  return FileText;
}

function blockLabel(block: ReportBlock) {
  if ('title' in block && block.title) return block.title;
  const labels = {
    text: 'Narrative',
    metrics: 'Metrics',
    table: 'Detailed table',
    organisation: 'Organisation chart',
    bars: 'Chart',
    swot: 'SWOT findings',
    forces: 'Five Forces',
    comparison: 'Impact chart',
    radar: 'Radar chart',
    positioning: 'Competitive positioning',
  };
  return labels[block.kind];
}

function nestedPageLabel(page: LayoutPage, index: number) {
  const heading = page.elements.find(
    (element) =>
      element.kind === 'text' &&
      element.y >= (page.width > page.height ? 150 : 176) &&
      element.y < page.height - 80 &&
      element.fontStyle.includes('bold') &&
      element.fontSize >= 16 &&
      element.text !== page.title
  );
  return heading?.kind === 'text' ? heading.text : index === 0 ? 'Opening page' : 'Continued';
}

function PagePreview({
  page,
  zoom = 'fit',
  presenting = false,
}: {
  page: LayoutPage;
  zoom?: string;
  presenting?: boolean;
}) {
  const stage = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 600, height: 650 });
  useEffect(() => {
    if (!stage.current) return;
    const observer = new ResizeObserver((entries) => {
      const bounds = entries[0].contentRect;
      setSize({ width: bounds.width, height: bounds.height });
    });
    observer.observe(stage.current);
    return () => observer.disconnect();
  }, []);
  const scale =
    zoom === 'fit'
      ? Math.min(size.width / page.width, size.height / page.height, presenting ? Infinity : 1)
      : zoom === 'width'
        ? Math.min(size.width / page.width, 1)
        : Number(zoom) / 100;
  const fitted = Math.max(0.1, scale);
  return (
    <div ref={stage} className={`report-preview-stage ${zoom === 'fit' ? 'is-fitted' : ''}`}>
      <div
        className="report-paper"
        style={{ width: page.width * fitted, height: page.height * fitted }}
      >
        <div
          style={{
            width: page.width,
            height: page.height,
            transform: `scale(${fitted})`,
            transformOrigin: 'top left',
          }}
        >
          <ReportCanvas page={page} />
        </div>
      </div>
    </div>
  );
}

function Drawer({
  open,
  onOpenChange,
  title,
  returnFocus,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  returnFocus: React.RefObject<HTMLButtonElement | null>;
  children: React.ReactNode;
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="report-dialog-overlay" />
        <Dialog.Content
          className="report-drawer report-builder"
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            returnFocus.current?.focus();
          }}
        >
          <div className="report-panel-heading">
            <Dialog.Title>{title}</Dialog.Title>
            <Dialog.Close className="report-icon-button" aria-label={`Close ${title}`}>
              <X size={18} />
            </Dialog.Close>
          </div>
          <Dialog.Description className="sr-only">
            Edit the order, content and appearance of your report.
          </Dialog.Description>
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function ControlPanel({
  tab,
  onChange,
  content,
  design,
}: {
  tab: 'content' | 'design';
  onChange: (tab: 'content' | 'design') => void;
  content: React.ReactNode;
  design: React.ReactNode;
}) {
  const id = useId();
  return (
    <>
      <div className="report-inspector-tabs" role="tablist" aria-label="Report controls">
        {(['content', 'design'] as const).map((value) => (
          <button
            type="button"
            key={value}
            role="tab"
            id={`${id}-${value}`}
            aria-controls={`${id}-panel`}
            aria-selected={tab === value}
            tabIndex={tab === value ? 0 : -1}
            onClick={() => onChange(value)}
            onKeyDown={(event) => {
              if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
              event.preventDefault();
              const next =
                event.key === 'Home'
                  ? 'content'
                  : event.key === 'End'
                    ? 'design'
                    : value === 'content'
                      ? 'design'
                      : 'content';
              onChange(next);
              document.getElementById(`${id}-${next}`)?.focus();
            }}
          >
            {value === 'content' ? 'Content' : 'Design'}
          </button>
        ))}
      </div>
      <div
        className="report-inspector-scroll"
        role="tabpanel"
        id={`${id}-panel`}
        aria-labelledby={`${id}-${tab}`}
        tabIndex={0}
      >
        {tab === 'design' ? design : content}
      </div>
    </>
  );
}

export default function VisualReportEditor(props: Props) {
  const draft = useReportDraft(props.initialDocument, {
    save: props.onSave,
    storageKey: props.draftKey,
    identityKey: props.draftIdentityKey,
    legacyStorageKey: props.legacyDraftKey,
    initiallySaved: props.initiallySaved,
    readOnly: props.readOnly,
  });
  const doc = draft.document;
  const editingLocked = !!props.readOnly || !!draft.recovery;
  const [view, setView] = useState<'report' | 'slides'>('report');
  const [selected, setSelected] = useState(
    doc.settings.includeCover ? 'cover' : doc.sections[0]?.id || 'design'
  );
  const [pageId, setPageId] = useState('');
  const [previewBlockId, setPreviewBlockId] = useState('');
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [zoom, setZoom] = useState('fit');
  const [preview, setPreview] = useState(false);
  const [drawer, setDrawer] = useState<'outline' | 'inspector' | null>(null);
  const [inspectorTab, setInspectorTab] = useState<'content' | 'design'>('content');
  const [busy, setBusy] = useState('');
  const [notice, setNotice] = useState('');
  const [suggestion, setSuggestion] = useState<{
    text: string;
    sectionId: string;
    slideId?: string;
  } | null>(null);
  const [presenting, setPresenting] = useState(false);
  const [presentIndex, setPresentIndex] = useState(0);
  const presentTrigger = useRef<HTMLButtonElement>(null);
  const outlineTrigger = useRef<HTMLButtonElement>(null);
  const inspectorTrigger = useRef<HTMLButtonElement>(null);
  const previousSlideIndex = useRef(0);
  const nativePresentationEntered = useRef(false);
  const pages = useMemo(() => compileReportLayout(doc, view), [doc, view]);
  const slidePages = useMemo(() => compileReportLayout(doc, 'slides'), [doc]);
  const slide = view === 'slides' ? doc.slides.find((item) => item.id === selected) : undefined;
  const sectionId = slide?.sectionId || (view === 'report' ? selected : '');
  const section = doc.sections.find((item) => item.id === sectionId);
  const selectedPages = pages.filter((page) =>
    view === 'slides' ? page.slideId === selected : page.sectionId === selected
  );
  const page = selectedPages.find((item) => item.id === pageId) || selectedPages[0];
  const pageIndex = page ? pages.indexOf(page) : -1;

  useEffect(() => {
    if (view !== 'slides') return;
    const index = doc.slides.findIndex((item) => item.id === selected);
    if (index >= 0) {
      previousSlideIndex.current = index;
      return;
    }
    const fallback = doc.slides[Math.min(previousSlideIndex.current, doc.slides.length - 1)];
    if (!fallback) return;
    setSelected(fallback.id);
    setPageId('');
    setPreviewBlockId('');
  }, [doc.slides, selected, view]);

  useEffect(() => {
    if (!previewBlockId) return;
    const chartPage = pages.find(
      (item) =>
        (view === 'slides' ? item.slideId === selected : item.sectionId === selected) &&
        item.elements.some((element) => 'blockId' in element && element.blockId === previewBlockId)
    );
    if (chartPage) setPageId(chartPage.id);
    setPreviewBlockId('');
  }, [pages, previewBlockId, selected, view]);

  useEffect(() => {
    void loadReportFonts().catch((failure) => setNotice(failure.message));
  }, []);
  useEffect(() => {
    if (!presenting) return;
    const navigate = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown' || event.key === ' ') {
        event.preventDefault();
        setPresentIndex((index) => Math.min(index + 1, slidePages.length - 1));
      }
      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        event.preventDefault();
        setPresentIndex((index) => Math.max(0, index - 1));
      }
      if (event.key === 'Home') setPresentIndex(0);
      if (event.key === 'End') setPresentIndex(slidePages.length - 1);
    };
    const fullscreen = () => {
      if (document.fullscreenElement === document.documentElement) {
        nativePresentationEntered.current = true;
      } else if (!document.fullscreenElement && nativePresentationEntered.current) {
        nativePresentationEntered.current = false;
        setPresenting(false);
      }
    };
    window.addEventListener('keydown', navigate);
    document.addEventListener('fullscreenchange', fullscreen);
    fullscreen();
    return () => {
      window.removeEventListener('keydown', navigate);
      document.removeEventListener('fullscreenchange', fullscreen);
    };
  }, [presenting, slidePages.length]);

  const selectPage = (index: number) => {
    const next = pages[index];
    if (!next) return;
    const id = view === 'slides' ? next.slideId || next.sectionId : next.sectionId;
    setSelected(id);
    setExpandedSections((current) => ({ ...current, [id]: true }));
    setPageId(next.id);
  };
  const select = (id: string) => {
    setSelected(id);
    setExpandedSections((current) => ({ ...current, [id]: true }));
    setPageId('');
    setInspectorTab('content');
    setDrawer(null);
  };
  const switchView = (next: 'report' | 'slides') => {
    if (next === view) return;
    const related =
      next === 'slides'
        ? doc.slides.find((item) => item.sectionId === sectionId)?.id || doc.slides[0]?.id
        : slide?.sectionId || 'cover';
    setView(next);
    setSelected(related || 'design');
    setPageId('');
  };
  const editSection = (change: Partial<NonNullable<typeof section>>) => {
    draft.update((current) => ({
      ...current,
      sections: current.sections.map((item) =>
        item.id === sectionId ? { ...item, ...change } : item
      ),
    }));
  };
  const editSlide = (change: Partial<NonNullable<typeof slide>>) => {
    draft.update((current) => ({
      ...current,
      slides: current.slides.map((item) => (item.id === selected ? { ...item, ...change } : item)),
    }));
  };
  const addSlide = (sourceSectionId: string) => {
    if (editingLocked || doc.slides.length >= 200) return;
    const sourceSection = doc.sections.find((item) => item.id === sourceSectionId);
    if (!sourceSection) return;
    const firstVisual =
      sourceSection.blocks.find((block) => block.optional) ||
      sourceSection.blocks.find((block) => block.kind !== 'text');
    const id = `slide-${crypto.randomUUID()}`;
    draft.update((current) => ({
      ...current,
      slides: [
        ...current.slides,
        {
          id,
          title: sourceSection.title.replace(/^Appendix:\s*/, ''),
          enabled: true,
          sectionId: sourceSection.id,
          blockIds: firstVisual ? [firstVisual.id] : [],
          takeaways: '',
          notes: '',
          layout: firstVisual ? 'visual' : 'narrative',
        },
      ],
    }));
    select(id);
    setPreviewBlockId(firstVisual?.id || '');
  };
  const toggleChart = (block: ReportBlock, included: boolean) => {
    if (slide)
      editSlide({
        blockIds: included
          ? [...slide.blockIds, block.id]
          : slide.blockIds.filter((id) => id !== block.id),
      });
    else if (section)
      editSection({
        blocks: section.blocks.map((item) =>
          item.id === block.id ? { ...item, enabled: included } : item
        ),
      });
    if (included) setPreviewBlockId(block.id);
  };
  const reorder = (id: string, direction: number) =>
    draft.update((current) => {
      const key = view === 'slides' ? 'slides' : 'sections';
      const items = [...current[key]];
      const index = items.findIndex((item) => item.id === id);
      if (index < 0 || index + direction < 0 || index + direction >= items.length) return current;
      [items[index], items[index + direction]] = [items[index + direction], items[index]];
      return { ...current, [key]: items } as ReportDocumentV2;
    });
  const refresh = async () => {
    if (editingLocked) return;
    setBusy('refresh');
    setNotice('');
    try {
      const source = await props.onRefresh();
      draft.update((current) => refreshReportSource(current, source));
      setNotice(
        source.loadErrors.length
          ? `Source refreshed with ${source.loadErrors.length} unavailable sources. Review the source notice.`
          : 'Source refreshed. Your commentary and slide selections are preserved.'
      );
    } catch (failure) {
      setNotice(
        failure instanceof Error
          ? failure.message
          : 'Source refresh failed. Your report is unchanged.'
      );
    } finally {
      setBusy('');
    }
  };
  const generate = async () => {
    if (!section || editingLocked || !isReportSectionSupported(section.id)) return;
    setBusy('ai');
    setNotice('');
    const targetId = section.id;
    const targetSlide = slide?.id;
    try {
      const text = await draftReportSection(doc, targetId, getSectionAnalysis(doc, targetId), view);
      setSuggestion({ text, sectionId: targetId, slideId: targetSlide });
    } catch (failure) {
      setNotice(
        failure instanceof Error
          ? failure.message
          : 'AI drafting failed. Your content is unchanged.'
      );
    } finally {
      setBusy('');
    }
  };
  const acceptSuggestion = () => {
    if (!suggestion) return;
    draft.update((current) =>
      suggestion.slideId
        ? {
            ...current,
            slides: current.slides.map((item) =>
              item.id === suggestion.slideId ? { ...item, takeaways: suggestion.text } : item
            ),
          }
        : {
            ...current,
            sections: current.sections.map((item) =>
              item.id === suggestion.sectionId
                ? {
                    ...item,
                    blocks: [
                      ...item.blocks,
                      {
                        id: crypto.randomUUID(),
                        kind: 'text',
                        title: 'Commentary',
                        markdown: suggestion.text,
                        authored: true,
                      },
                    ],
                  }
                : item
            ),
          }
    );
    setSuggestion(null);
  };
  const download = async (format: 'pdf' | 'pptx', target: 'report' | 'slides') => {
    setBusy('export');
    setNotice('');
    try {
      const { exportReport } = await import('../reports/export');
      await exportReport(doc, compileReportLayout(doc, target), format);
      setNotice('Your download is ready. It includes the current draft.');
    } catch (failure) {
      setNotice(
        failure instanceof Error
          ? failure.message
          : 'The export could not be created. Please try again.'
      );
    } finally {
      setBusy('');
    }
  };
  const openPresentation = () => {
    const index =
      view === 'slides' && page ? slidePages.findIndex((item) => item.id === page.id) : 0;
    setPresentIndex(Math.max(0, index));
    nativePresentationEntered.current = false;
    setPresenting(true);
    if (document.fullscreenEnabled !== false)
      void document.documentElement.requestFullscreen?.().catch(() => {
        /* The fullscreen dialog also works where the browser blocks fullscreen. */
      });
  };
  const closePresentation = (open: boolean) => {
    setPresenting(open);
    if (!open) {
      nativePresentationEntered.current = false;
      if (document.fullscreenElement) void document.exitFullscreen().catch(() => {});
    }
  };

  const outlineRow = (
    id: string,
    title: string,
    enabled: boolean,
    index: number,
    total: number,
    fixed = false
  ) => {
    const related = pages.filter((item) =>
      view === 'slides' ? item.slideId === id : item.sectionId === id
    );
    const sourceId =
      view === 'slides' ? doc.slides.find((item) => item.id === id)?.sectionId || id : id;
    const style = reportSectionStyle(sourceId);
    const Icon = sectionIcon(sourceId);
    const expanded = related.length > 1 && (expandedSections[id] ?? selected === id);
    const rowLabel = `${fixed ? '' : `${String(index + 1).padStart(2, '0')} `}${title} ${!enabled ? 'Hidden' : related.length > 1 ? `${related.length} ${view === 'slides' ? 'slides' : 'pages'}` : related[0] ? `${view === 'slides' ? 'Slide' : 'Page'} ${related[0].pageNumber}` : 'No content'}`;
    return (
      <div
        key={id}
        className={`report-outline-entry ${selected === id ? 'is-selected' : ''} ${!enabled ? 'is-hidden' : ''}`}
        style={{ '--section-color': style.color, '--section-tint': style.tint } as CSSProperties}
      >
        <div className="report-outline-row">
          <button
            type="button"
            className="report-outline-select"
            onClick={() => select(id)}
            aria-label={rowLabel}
            aria-current={selected === id && related.length < 2 ? 'page' : undefined}
          >
            <span className="report-outline-symbol" aria-hidden="true">
              <Icon size={14} strokeWidth={1.8} />
            </span>
            <span className="report-outline-title">{title.replace(/^Appendix:\s*/, '')}</span>
            <span className="report-outline-count" aria-hidden="true">
              {!enabled
                ? '—'
                : related.length > 1
                  ? `${related[0].pageNumber}–${related[related.length - 1].pageNumber}`
                  : related[0]?.pageNumber || '—'}
            </span>
          </button>
          {related.length > 1 && (
            <button
              type="button"
              className="report-icon-button report-outline-disclosure"
              aria-label={`${expanded ? 'Collapse' : 'Expand'} ${title} pages`}
              aria-expanded={expanded}
              onClick={() => setExpandedSections((current) => ({ ...current, [id]: !expanded }))}
            >
              <ChevronRight size={12} />
            </button>
          )}
          {!fixed && (
            <div className="report-outline-move">
              <button
                type="button"
                className="report-icon-button"
                aria-label={`Move ${title} up`}
                disabled={editingLocked || index === 0}
                onClick={() => reorder(id, -1)}
              >
                <ArrowUp size={12} />
              </button>
              <button
                type="button"
                className="report-icon-button"
                aria-label={`Move ${title} down`}
                disabled={editingLocked || index === total - 1}
                onClick={() => reorder(id, 1)}
              >
                <ArrowDown size={12} />
              </button>
            </div>
          )}
        </div>
        {expanded && (
          <div className="report-outline-pages" role="group" aria-label={`${title} pages`}>
            {related.map((child, childIndex) => (
              <button
                type="button"
                key={child.id}
                className="report-outline-page"
                aria-label={`${view === 'slides' ? 'Slide' : 'Page'} ${child.pageNumber}: ${title}, ${nestedPageLabel(child, childIndex)}`}
                aria-current={page?.id === child.id ? 'page' : undefined}
                onClick={() => {
                  selectPage(pages.indexOf(child));
                  setDrawer(null);
                }}
              >
                <span className="report-outline-page-number">{child.pageNumber}</span>
                <span>{nestedPageLabel(child, childIndex)}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };
  const outline = (
    <>
      <div className="report-panel-intro">
        <div className="report-outline-heading">
          <span>{view === 'report' ? 'Report structure' : 'Slide sequence'}</span>
          <span className="report-outline-total">{pages.length}</span>
        </div>
        <p>
          {view === 'report'
            ? `${doc.sections.filter((item) => item.enabled).length} sections · ${pages.length} pages`
            : `${doc.slides.filter((item) => item.enabled).length} entries · ${pages.length} slides`}
        </p>
        {view === 'slides' && (
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <button
                type="button"
                className="report-outline-expand-all"
                disabled={editingLocked || doc.slides.length >= 200}
                aria-label="Add slide from report section"
              >
                <Plus size={12} /> Add slide
              </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content
                className="report-export-menu report-section-menu"
                sideOffset={6}
              >
                <DropdownMenu.Label>Choose a report section</DropdownMenu.Label>
                {doc.sections.map((item) => (
                  <DropdownMenu.Item key={item.id} onSelect={() => addSlide(item.id)}>
                    {item.title.replace(/^Appendix:\s*/, '')}
                  </DropdownMenu.Item>
                ))}
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        )}
        <button
          type="button"
          className="report-outline-expand-all"
          onClick={() => {
            const items =
              view === 'report'
                ? ['cover', 'contents', ...doc.sections.map((item) => item.id)]
                : doc.slides.map((item) => item.id);
            const expand = !items.every((id) => expandedSections[id]);
            setExpandedSections(Object.fromEntries(items.map((id) => [id, expand])));
          }}
        >
          <List size={12} />
          {(view === 'report'
            ? ['cover', 'contents', ...doc.sections.map((item) => item.id)]
            : doc.slides.map((item) => item.id)
          ).every((id) => expandedSections[id])
            ? 'Collapse all pages'
            : 'Show all pages'}
        </button>
      </div>
      <nav
        className="report-outline-list"
        aria-label={view === 'report' ? 'Report outline' : 'Slide outline'}
      >
        {view === 'report' && (
          <>
            {outlineRow('cover', 'Cover', doc.settings.includeCover, -2, doc.sections.length, true)}
            {outlineRow(
              'contents',
              'Contents',
              doc.settings.includeContents,
              -1,
              doc.sections.length,
              true
            )}
          </>
        )}
        {view === 'slides'
          ? doc.slides.map((item, index) =>
              outlineRow(item.id, item.title, item.enabled, index, doc.slides.length)
            )
          : doc.sections.map((item, index) => (
              <div key={item.id}>
                {item.appendix && !doc.sections[index - 1]?.appendix && (
                  <p className="report-outline-divider">Appendices</p>
                )}
                {outlineRow(item.id, item.title, item.enabled, index, doc.sections.length)}
              </div>
            ))}
      </nav>
      <div className="report-outline-footer">
        <span className="report-eyebrow">Source snapshot</span>
        <p>
          {new Date(doc.sourceSnapshot.capturedAt).toLocaleDateString(undefined, {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          })}
        </p>
        <button
          type="button"
          className="report-text-button"
          disabled={!!busy || editingLocked}
          onClick={() => void refresh()}
        >
          <RefreshCw size={13} />
          {busy === 'refresh' ? 'Refreshing…' : 'Refresh source data'}
        </button>
      </div>
    </>
  );

  const design = (
    <div className="report-inspector-fields">
      <p className="report-field-help">
        A consistent company identity across your report and deck.
      </p>
      <div className="report-brand-palette">
        <span>
          <i style={{ background: REPORT_ACCENT }} />
          <i style={{ background: '#8934ed' }} />
          StratPlan palette
        </span>
        <button
          type="button"
          className="report-text-button"
          disabled={editingLocked || doc.theme.accent === REPORT_ACCENT}
          onClick={() =>
            draft.update((current) => ({ ...current, theme: { accent: REPORT_ACCENT } }))
          }
        >
          Use brand colour
        </button>
      </div>
      <label>
        Report title
        <input
          maxLength={500}
          value={doc.metadata.title}
          disabled={editingLocked}
          onChange={(event) =>
            draft.update((current) => ({
              ...current,
              metadata: { ...current.metadata, title: event.target.value },
            }))
          }
        />
      </label>
      <label>
        Subtitle
        <textarea
          maxLength={500}
          rows={3}
          value={doc.metadata.subtitle}
          disabled={editingLocked}
          onChange={(event) =>
            draft.update((current) => ({
              ...current,
              metadata: { ...current.metadata, subtitle: event.target.value },
            }))
          }
        />
      </label>
      <div className="report-field-pair">
        <label>
          Date
          <input
            type="date"
            value={doc.metadata.date}
            disabled={editingLocked}
            onChange={(event) =>
              draft.update((current) => ({
                ...current,
                metadata: { ...current.metadata, date: event.target.value },
              }))
            }
          />
        </label>
        <label>
          Accent colour
          <input
            type="color"
            value={doc.theme.accent}
            disabled={editingLocked}
            onChange={(event) =>
              draft.update((current) => ({ ...current, theme: { accent: event.target.value } }))
            }
          />
        </label>
      </div>
      <label>
        Prepared by
        <input
          maxLength={500}
          value={doc.metadata.author}
          disabled={editingLocked}
          onChange={(event) =>
            draft.update((current) => ({
              ...current,
              metadata: { ...current.metadata, author: event.target.value },
            }))
          }
        />
      </label>
      <div className="report-company-brand">
        {doc.sourceSnapshot.company.logo ? (
          <>
            <img
              src={doc.sourceSnapshot.company.logo}
              alt={`${doc.sourceSnapshot.company.name} logo`}
            />
            <span>
              <strong>Company logo included</strong>
              <small>Shown on the cover and page headers.</small>
            </span>
          </>
        ) : (
          <>
            <BookOpen size={20} />
            <span>
              <strong>{doc.sourceSnapshot.company.name}</strong>
              <small>Add a logo in company settings, then refresh source data.</small>
            </span>
          </>
        )}
      </div>
      {(
        [
          { key: 'includeCover', label: 'Include cover' },
          { key: 'includeContents', label: 'Include contents in report' },
          { key: 'includePageNumbers', label: 'Page and slide numbers' },
          { key: 'includeFooter', label: 'StratPlan footer credit' },
          { key: 'confidential', label: 'Mark as confidential' },
        ] as const
      ).map((item) => (
        <label className="report-checkbox" key={item.key}>
          <input
            type="checkbox"
            checked={doc.settings[item.key]}
            disabled={editingLocked}
            onChange={(event) =>
              draft.update((current) => ({
                ...current,
                settings: { ...current.settings, [item.key]: event.target.checked },
              }))
            }
          />
          {item.label}
        </label>
      ))}
    </div>
  );
  const content = (
    <div className="report-inspector-fields">
      {section || slide ? (
        <>
          <label>
            {view === 'slides' ? 'Slide title' : 'Section title'}
            <input
              maxLength={500}
              value={slide?.title || section?.title || ''}
              disabled={editingLocked}
              onChange={(event) =>
                slide
                  ? editSlide({ title: event.target.value })
                  : editSection({ title: event.target.value })
              }
            />
          </label>
          <label className="report-checkbox">
            <input
              type="checkbox"
              checked={slide?.enabled ?? section?.enabled ?? false}
              disabled={editingLocked}
              onChange={(event) =>
                slide
                  ? editSlide({ enabled: event.target.checked })
                  : editSection({ enabled: event.target.checked })
              }
            />
            Include in {view === 'slides' ? 'deck' : 'report'}
          </label>
          <label>
            Guided layout
            <select
              value={slide?.layout || section?.layout}
              disabled={editingLocked}
              onChange={(event) =>
                slide
                  ? editSlide({ layout: event.target.value as ReportLayout })
                  : editSection({ layout: event.target.value as ReportLayout })
              }
            >
              {layouts.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>
          {section?.blocks.some((block) => block.optional) && (
            <fieldset className="report-detail-selection report-chart-selection">
              <legend>
                <BarChart3 size={13} /> Visual charts
              </legend>
              <p className="report-field-help">
                From your strategy summary. Choose charts for this {slide ? 'slide' : 'section'}.
              </p>
              {section.blocks
                .filter((block) => block.optional)
                .map((block) => (
                  <label key={block.id} className="report-checkbox">
                    <input
                      type="checkbox"
                      aria-label={blockLabel(block)}
                      checked={slide ? slide.blockIds.includes(block.id) : block.enabled !== false}
                      disabled={editingLocked}
                      onChange={(event) => toggleChart(block, event.target.checked)}
                    />
                    <span>
                      {blockLabel(block)}
                      {'caption' in block && <small>{block.caption}</small>}
                    </span>
                  </label>
                ))}
            </fieldset>
          )}
          {slide ? (
            <>
              <label className="report-editor-label">
                Slide wording
                <span className="report-field-help">
                  Keep the main message concise. Full report prose stays separate.
                </span>
              </label>
              <div data-color-mode="light">
                <MDEditor
                  value={slide.takeaways}
                  onChange={(value) => editSlide({ takeaways: value || '' })}
                  preview="edit"
                  commands={markdownCommands}
                  extraCommands={[]}
                  height={180}
                  textareaProps={{
                    'aria-label': 'Slide wording',
                    maxLength: 80000,
                    disabled: editingLocked,
                  }}
                />
              </div>
              {section && (
                <fieldset className="report-detail-selection">
                  <legend>Supporting detail</legend>
                  {section.blocks
                    .filter((block) => !block.optional)
                    .map((block) => (
                      <label key={block.id} className="report-checkbox">
                        <input
                          type="checkbox"
                          checked={slide.blockIds.includes(block.id)}
                          disabled={editingLocked}
                          onChange={(event) =>
                            editSlide({
                              blockIds: event.target.checked
                                ? [...slide.blockIds, block.id]
                                : slide.blockIds.filter((id) => id !== block.id),
                            })
                          }
                        />
                        {blockLabel(block)}
                      </label>
                    ))}
                </fieldset>
              )}
              <label>
                Speaker notes
                <textarea
                  maxLength={80000}
                  rows={5}
                  value={slide.notes}
                  disabled={editingLocked}
                  onChange={(event) => editSlide({ notes: event.target.value })}
                  placeholder="Notes for the presenter…"
                />
                <span className="report-field-help">
                  Included in PowerPoint notes. Kept off the audience’s slides and PDFs.
                </span>
              </label>
            </>
          ) : (
            section && (
              <>
                {section.blocks.some((block) => block.kind !== 'text' && !block.optional) && (
                  <fieldset className="report-detail-selection">
                    <legend>Supporting detail</legend>
                    {section.blocks
                      .filter((block) => block.kind !== 'text' && !block.optional)
                      .map((block) => (
                        <label key={block.id} className="report-checkbox">
                          <input
                            type="checkbox"
                            checked={block.enabled !== false}
                            disabled={editingLocked}
                            onChange={(event) =>
                              editSection({
                                blocks: section.blocks.map((item) =>
                                  item.id === block.id
                                    ? { ...item, enabled: event.target.checked }
                                    : item
                                ),
                              })
                            }
                          />
                          {blockLabel(block)}
                        </label>
                      ))}
                  </fieldset>
                )}
                {section.blocks
                  .filter((block) => block.kind === 'text')
                  .map(
                    (block) =>
                      block.kind === 'text' && (
                        <div key={block.id} className="report-narrative-field">
                          <label className="report-editor-label">
                            {block.title || 'Narrative'}
                            <span className="report-field-help">
                              {block.authored
                                ? 'Your authored commentary'
                                : 'Editing marks this text as authored, so source refresh preserves it.'}
                            </span>
                          </label>
                          <div data-color-mode="light">
                            <MDEditor
                              value={block.markdown}
                              onChange={(value) =>
                                editSection({
                                  blocks: section.blocks.map((item) =>
                                    item.id === block.id && item.kind === 'text'
                                      ? { ...item, markdown: value || '', authored: true }
                                      : item
                                  ),
                                })
                              }
                              preview="edit"
                              commands={markdownCommands}
                              extraCommands={[]}
                              height={200}
                              textareaProps={{
                                'aria-label': block.title || 'Section narrative',
                                maxLength: 80000,
                                disabled: editingLocked,
                              }}
                            />
                          </div>
                        </div>
                      )
                  )}
                <button
                  type="button"
                  className="report-secondary-button"
                  disabled={editingLocked}
                  onClick={() =>
                    editSection({
                      blocks: [
                        ...section.blocks,
                        {
                          id: crypto.randomUUID(),
                          kind: 'text',
                          title: 'Commentary',
                          markdown: '',
                          authored: true,
                        },
                      ],
                    })
                  }
                >
                  <Plus size={14} />
                  Add commentary
                </button>
                <p className="report-field-help">
                  Tables and charts use the recorded strategy snapshot. Refresh source data to
                  update them.
                </p>
                <button
                  type="button"
                  className="report-text-button"
                  disabled={editingLocked || doc.slides.length >= 200}
                  onClick={() => {
                    setView('slides');
                    addSlide(section.id);
                  }}
                >
                  <Plus size={14} />
                  Add section to deck
                </button>
              </>
            )
          )}
          {section && isReportSectionSupported(section.id) && (
            <button
              type="button"
              className="report-ai-button"
              onClick={() => void generate()}
              disabled={!!busy || editingLocked}
            >
              <Sparkles size={14} />
              {busy === 'ai' ? 'Drafting suggestion…' : 'Draft with AI'}
            </button>
          )}
        </>
      ) : (
        <>
          <h3>{selected === 'contents' ? 'Contents' : 'Your opening impression'}</h3>
          <p className="report-field-help">
            {selected === 'contents'
              ? 'Page numbers are calculated from the finished report, including continuation pages.'
              : 'A simple, confident cover with your company logo, report title and accent colour.'}
          </p>
          <button
            type="button"
            className="report-secondary-button"
            onClick={() => setInspectorTab('design')}
          >
            Edit report details
          </button>
        </>
      )}
    </div>
  );
  const inspector = (
    <ControlPanel tab={inspectorTab} onChange={setInspectorTab} content={content} design={design} />
  );
  const saveLabel = props.readOnly
    ? 'View only'
    : draft.status === 'saving'
      ? 'Saving…'
      : draft.status === 'error'
        ? 'Save failed · draft retained'
        : draft.status === 'conflict'
          ? 'Newer version available'
          : draft.dirty
            ? 'Unsaved changes'
            : draft.status === 'saved'
              ? 'All changes saved'
              : 'Ready to save';

  return (
    <div className={`report-builder ${preview ? 'is-previewing' : ''}`}>
      <div className="report-toolbar">
        <div className="report-view-switch" aria-label="Document format">
          <button
            type="button"
            aria-pressed={view === 'report'}
            onClick={() => switchView('report')}
          >
            <FileText size={15} />
            Report
          </button>
          <button
            type="button"
            aria-pressed={view === 'slides'}
            onClick={() => switchView('slides')}
          >
            <Layers size={15} />
            Slides
          </button>
        </div>
        <div className="report-history">
          <button
            type="button"
            className="report-icon-button"
            aria-label="Undo"
            disabled={!draft.canUndo}
            onClick={draft.undo}
          >
            <Undo2 size={16} />
          </button>
          <button
            type="button"
            className="report-icon-button"
            aria-label="Redo"
            disabled={!draft.canRedo}
            onClick={draft.redo}
          >
            <Redo2 size={16} />
          </button>
        </div>
        <span className={`report-save-status ${draft.status}`} role="status" aria-live="polite">
          {draft.status === 'saving' ? (
            <Loader2 size={12} className="animate-spin" />
          ) : draft.status === 'saved' && !draft.dirty ? (
            <Check size={12} />
          ) : null}
          {saveLabel}
        </span>
        <div className="report-toolbar-actions">
          <button
            type="button"
            className="report-icon-button report-outline-trigger"
            ref={outlineTrigger}
            aria-label="Open outline"
            onClick={() => setDrawer('outline')}
          >
            <List size={18} />
          </button>
          <button
            type="button"
            className="report-icon-button report-inspector-trigger"
            ref={inspectorTrigger}
            aria-label="Open content controls"
            onClick={() => setDrawer('inspector')}
          >
            <PanelRight size={18} />
          </button>
          <button
            type="button"
            className="report-secondary-button report-preview-button"
            aria-label={preview ? 'Return to editor' : 'Preview'}
            aria-pressed={preview}
            onClick={() => setPreview((value) => !value)}
          >
            <Maximize size={14} />
            <span>{preview ? 'Edit' : 'Preview'}</span>
          </button>
          <button
            type="button"
            className="report-secondary-button report-save-button"
            aria-label="Save"
            disabled={
              props.readOnly ||
              draft.status === 'saving' ||
              draft.status === 'conflict' ||
              !!draft.recovery
            }
            onClick={() => void draft.save()}
          >
            <Save size={14} />
            <span>Save</span>
          </button>
          <button
            type="button"
            ref={presentTrigger}
            aria-label="Present"
            className="report-primary-button"
            disabled={!slidePages.length}
            onClick={openPresentation}
          >
            <Play size={14} />
            <span>Present</span>
          </button>
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <button
                type="button"
                className="report-secondary-button"
                disabled={!!busy}
                aria-label="Export report or slides"
              >
                {busy === 'export' ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Download size={14} />
                )}
                <span className="report-export-label">Export</span>
                <ChevronDown size={12} />
              </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content className="report-export-menu" align="end" sideOffset={6}>
                <DropdownMenu.Label>Download current draft</DropdownMenu.Label>
                <DropdownMenu.Item onSelect={() => void download('pdf', 'report')}>
                  A4 report · PDF
                </DropdownMenu.Item>
                <DropdownMenu.Item onSelect={() => void download('pdf', 'slides')}>
                  16:9 slides · PDF
                </DropdownMenu.Item>
                <DropdownMenu.Item onSelect={() => void download('pptx', 'slides')}>
                  Editable PowerPoint · PPTX
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        </div>
      </div>
      {draft.recovery && (
        <div className="report-notice report-notice-warning" role="alert">
          <p>A retained draft is available on this device. Restore it to review before saving.</p>
          <button type="button" onClick={draft.restore} disabled={props.readOnly}>
            Restore draft
          </button>
          <button type="button" onClick={draft.discardRecovery}>
            Discard retained draft
          </button>
        </div>
      )}
      {draft.reviewPending && (
        <div className="report-notice report-notice-warning" role="status">
          <p>
            Your retained draft is restored. Review the changes, then save to resume autosaving.
          </p>
        </div>
      )}
      {(draft.status === 'error' || draft.status === 'conflict') && (
        <div className="report-notice report-notice-warning" role="alert">
          <p>
            {draft.error} Your draft is retained.{' '}
            {draft.status === 'conflict' &&
              'Download your current draft before reloading the saved version.'}
          </p>
          <button
            type="button"
            onClick={draft.status === 'conflict' ? props.onReload : () => void draft.save()}
          >
            {draft.status === 'conflict' ? 'Reload saved version' : 'Retry save'}
          </button>
        </div>
      )}
      {!!doc.sourceSnapshot.loadErrors.length && (
        <details className="report-source-warning">
          <summary>
            {doc.sourceSnapshot.loadErrors.length} sources unavailable in this snapshot
          </summary>
          <ul>
            {doc.sourceSnapshot.loadErrors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </details>
      )}
      {notice && (
        <div className="report-notice" role="status">
          <p>{notice}</p>
          <button
            type="button"
            className="report-icon-button"
            onClick={() => setNotice('')}
            aria-label="Dismiss notice"
          >
            <X size={14} />
          </button>
        </div>
      )}
      <div className="report-workspace">
        <aside className="report-outline" aria-label="Document outline">
          {outline}
        </aside>
        <main className="report-preview" aria-label="Document preview">
          <div className="report-preview-heading">
            <div>
              <span className="report-eyebrow report-preview-category">
                <i style={{ background: reportSectionStyle(sectionId || 'cover').color }} />
                {view === 'report' ? 'A4 report' : '16:9 presentation'}
                <span>· {reportSectionStyle(sectionId || 'cover').label}</span>
              </span>
              <h2>{page?.title || slide?.title || section?.title || 'Document preview'}</h2>
            </div>
            <label className="report-zoom">
              <span className="sr-only">Preview zoom</span>
              <select value={zoom} onChange={(event) => setZoom(event.target.value)}>
                <option value="fit">Fit</option>
                <option value="width">Fit width</option>
                <option value="50">50%</option>
                <option value="75">75%</option>
                <option value="100">100%</option>
              </select>
            </label>
          </div>
          {page ? (
            <PagePreview page={page} zoom={zoom} />
          ) : (
            <div className="report-empty-preview">
              <FileText size={32} />
              <h3>This item is hidden</h3>
              <p>Enable it in the content or design controls to include it in your document.</p>
            </div>
          )}
          <div className="report-page-navigation">
            <button
              type="button"
              className="report-icon-button"
              aria-label="Previous page"
              disabled={pageIndex <= 0}
              onClick={() => selectPage(pageIndex - 1)}
            >
              <ChevronLeft size={17} />
            </button>
            <span>
              {page
                ? `${view === 'report' ? 'Page' : 'Slide'} ${pageIndex + 1} of ${pages.length}`
                : `${pages.length} ${view === 'report' ? 'pages' : 'slides'}`}
            </span>
            <button
              type="button"
              className="report-icon-button"
              aria-label="Next page"
              disabled={pageIndex === pages.length - 1 || !pages.length}
              onClick={() => selectPage(pageIndex + 1)}
            >
              <ChevronRight size={17} />
            </button>
          </div>
        </main>
        <aside className="report-inspector" aria-label="Content and design controls">
          {inspector}
        </aside>
      </div>
      <Drawer
        title="Outline"
        returnFocus={outlineTrigger}
        open={drawer === 'outline'}
        onOpenChange={(open) => setDrawer(open ? 'outline' : null)}
      >
        {outline}
      </Drawer>
      <Drawer
        title="Content & design"
        returnFocus={inspectorTrigger}
        open={drawer === 'inspector'}
        onOpenChange={(open) => setDrawer(open ? 'inspector' : null)}
      >
        {inspector}
      </Drawer>
      <Dialog.Root
        open={!!suggestion}
        onOpenChange={(open) => {
          if (!open) setSuggestion(null);
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="report-dialog-overlay" />
          <Dialog.Content className="report-suggestion report-builder">
            <div className="report-panel-heading">
              <Dialog.Title>Review AI suggestion</Dialog.Title>
              <Dialog.Close className="report-icon-button" aria-label="Close suggestion">
                <X size={18} />
              </Dialog.Close>
            </div>
            <Dialog.Description>
              Based on your recorded snapshot. Review the wording and facts before applying.
            </Dialog.Description>
            <div className="report-suggestion-text">
              <ReactMarkdown>{suggestion?.text || ''}</ReactMarkdown>
            </div>
            <div className="report-suggestion-actions">
              <Dialog.Close className="report-secondary-button">Discard</Dialog.Close>
              <button
                type="button"
                className="report-primary-button"
                disabled={editingLocked}
                onClick={acceptSuggestion}
              >
                {suggestion?.slideId ? 'Replace slide wording' : 'Add as commentary'}
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
      <Dialog.Root open={presenting} onOpenChange={closePresentation}>
        <Dialog.Portal>
          <Dialog.Overlay className="report-presentation-overlay" />
          <Dialog.Content
            className="report-presentation"
            onCloseAutoFocus={(event) => {
              event.preventDefault();
              presentTrigger.current?.focus();
            }}
          >
            <Dialog.Title className="sr-only">Strategy presentation</Dialog.Title>
            <Dialog.Description className="sr-only">
              Use the arrow keys to navigate. Press Escape to return to the editor.
            </Dialog.Description>
            {slidePages[Math.min(presentIndex, slidePages.length - 1)] && (
              <PagePreview
                page={slidePages[Math.min(presentIndex, slidePages.length - 1)]}
                presenting
              />
            )}
            <div className="report-presentation-controls">
              <button
                type="button"
                className="report-icon-button"
                aria-label="Previous slide"
                disabled={presentIndex <= 0}
                onClick={() => setPresentIndex((index) => Math.max(0, index - 1))}
              >
                <ChevronLeft size={22} />
              </button>
              <span>
                {presentIndex + 1} / {slidePages.length}
              </span>
              <button
                type="button"
                className="report-icon-button"
                aria-label="Next slide"
                disabled={presentIndex >= slidePages.length - 1}
                onClick={() =>
                  setPresentIndex((index) => Math.min(slidePages.length - 1, index + 1))
                }
              >
                <ChevronRight size={22} />
              </button>
              <Dialog.Close className="report-icon-button" aria-label="Exit presentation">
                <X size={20} />
              </Dialog.Close>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}
